import { useEffect, useState } from 'react';
import { api, darf, menge, type Anmelder, datum} from '../api.ts';
import {
  Auswahl, Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import {
  feldname, formelsatz, massAus, massHinaus, mengeGesperrt, rollentext,
  zulagesatz, type Massfeldanzeige,
} from '../teile/masse.ts';
import { fragen } from '../teile/rueckfrage.tsx';

// Das Aufmass eines Auftrags.
//
// Der Befund vom 11.08.2026: in allen 63 eingereichten Zeilen war die Menge
// des Innendienstes identisch mit der des Subunternehmers. Nicht aus
// Nachlaessigkeit, sondern weil es nichts zum Gegenhalten gab. Deshalb stehen
// hier beide Mengen NEBENEINANDER, und eine Abweichung ohne Begruendung nimmt
// der Server nicht an.

interface Befund { text: string; hart: boolean; abschnittId?: string | null }

export default function Aufmass(
  { ich, auftragId }: { ich: Anmelder; auftragId: string }
) {
  const [stand, setStand] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [lv, setLv] = useState<any[]>([]);
  const [dialog, setDialog] = useState<
    null | 'neu' | 'vorschlag' | 'absenden' | 'oeffnen' | 'abschnitt'>(null);

  const darfPruefen = darf(ich, 'aufmass.pruefen');
  const darfErfassen = darf(ich, 'aufmass.erfassen') || darfPruefen;
  // EINE SPALTE, DIE FÜR EINEN LESER IMMER LEER IST, IST KEINE SPALTE.
  //
  // `menge_mds` ist für einen Sub überall auf NULL maskiert - und die Tabelle
  // hatte trotzdem eine Überschrift „MDS" darüber. Er hat eine Menge
  // eingetragen und danach zwei leere Zahlenspalten gesehen (die zweite, weil
  // sein Wert bis zum 26.08.2026 in der FALSCHEN landete). Für ihn heisst die
  // Spalte jetzt schlicht „Menge", und die geprüfte fällt weg.
  const istSub = ich.istSub;

  async function laden() {
    try { setStand(await api.aufmass(auftragId)); setFehler(null); }
    catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); api.lv().then(setLv).catch(() => {}); }, [auftragId]);
  useFrisch(laden);

  /* WAS NACH DEM ABSENDEN MIT DEM TIEFBAU GESCHAH (23.09.2026). Marvin: „das
     problem ist auch beim tiefbau." Ist das Gewerk noch nicht fertig
     gemeldet, geht es mit dem Absenden mit - fehlt dafür ein Nachweis,
     steht hier welcher. */
  const [nachAbsenden, setNachAbsenden] = useState<string | null>(null);

  /** Ein Aufruf, der die Antwort gleich als neuen Stand nimmt. */
  async function tu(was: () => Promise<any>) {
    setLaeuft(true); setFehler(null);
    try {
      const neu = await was();
      if (neu?.positionen) setStand(neu); else await laden();
      setDialog(null);
      return true;
    } catch (f) { setFehler(ausFehler(f)); return false; }
    finally { setLaeuft(false); }
  }

  if (!stand) {
    return <div className="seite"><Fehlerkasten lage={fehler} /></div>;
  }

  const k = stand.kopf;
  const eingereicht = k.aufmass_status === 'eingereicht';
  /* UND EIN ABGESCHLOSSENES AUFMASS LIESS SICH HIER GAR NICHT MEHR ÖFFNEN.
   *
   * ORIGINALTEXT MARVIN (31.08.2026)
   *   „wie kann ich dem sub (egal welchem) den auftrag nochmal freigeben,
   *    falls er das aufmaß oder bilder anpassen muss?"
   *
   * DEN WEG GAB ES, und er lag falsch: „Vorgang → Freigeben" in der
   * Werkzeugleiste der Akte - also zwei Klappmenüs entfernt von dem
   * Bildschirm, auf dem der Zustand steht. Auf DIESEM Reiter stand bei
   * `eingereicht` ein Knopf und bei `abgeschlossen` gar nichts: ein
   * Bildschirm ohne einen einzigen Knopf sieht aus wie eine Sackgasse.
   *
   * Beide Zustände bekommen ihn jetzt, und der Knopf sagt, aus welchem er
   * herausführt. Der Weg dahinter ist derselbe wie im Vorgang-Menü
   * (`doku.ts`), samt Begründungszwang: **wer etwas wieder aufmacht, das ein
   * Sub abgegeben hat, schuldet ihm den Satz warum.** */
  const zu = eingereicht || k.aufmass_status === 'abgeschlossen';
  const abschnittName = (id: string | null) =>
    stand.abschnitte.find((a: any) => a.id === id)?.bezeichnung ?? 'ohne Abschnitt';

  // Nach Abschnitt gruppiert. Das ist die Ordnung, in der auch geprueft wird.
  const gruppen: { id: string | null; name: string; zeilen: any[] }[] = [];
  for (const p of stand.positionen) {
    const id = p.bauabschnitt_id ?? null;
    let g = gruppen.find((x) => x.id === id);
    if (!g) { g = { id, name: abschnittName(id), zeilen: [] }; gruppen.push(g); }
    g.zeilen.push(p);
  }
  /* EIN ABSCHNITT OHNE POSTEN STAND AUF KEINEM BILDSCHIRM.
     ======================================================================
     Die Gruppen entstanden allein aus den POSITIONEN. Ein frisch angelegter
     Abschnitt hat keine, also erschien er in dieser Tabelle nie - er stand
     nur in der Auswahlliste von „Position hinzufügen". Wer sich vertippt
     hatte, sah seinen Fehler nirgends und konnte ihn nirgends wegnehmen.

     **EIN ZIRKEL LÖST SICH NIE VON ALLEIN AUF:** löschen lässt sich nur ein
     LEERER Abschnitt, und genau der war unsichtbar. Er steht jetzt mit
     seiner eigenen Zeile da, und dort steht auch der einzige Knopf, der ihn
     wieder wegnimmt. */
  for (const a of stand.abschnitte) {
    if (!gruppen.some((g) => g.id === a.id)) {
      gruppen.push({ id: a.id, name: a.bezeichnung, zeilen: [] });
    }
  }

  return (
    <div>
      <div className="reihe" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        <div className="reihe" style={{ gap: 8 }}>
          {/* DER ZUSTANDSPUNKT STAND IM KOMMENTAR UND NICHT IM MARKUP.
              Der Absatz weiter unten verspricht ihn seit dem 03.09.2026
              („dort steht seither ein Zustandspunkt vor jedem Titel") - hier
              stand keiner. Er ist die Antwort auf „muss ich hier hinsehen",
              und die kommt vor dem Namen. */}
          <span className={'zustandspunkt '
            + (stand.befunde.some((b: Befund) => b.hart) ? 'hart'
             : stand.befunde.length ? 'offen'
             : stand.positionen.length ? 'gut' : 'aus')}
                title={stand.befunde.some((b: Befund) => b.hart)
                  ? 'Hält auf' : stand.befunde.length ? 'Zum Nachsehen'
                  : stand.positionen.length ? 'Keine Beanstandung'
                  : 'Noch nichts erfasst'} />
          <b>Aufmaß</b>{' '}
          {/* „OFFEN" STAND AUCH BEI EINEM ABGESCHLOSSENEN AUFMASS (.403):
              der Merker kannte nur eingereicht oder nicht. Daneben stand
              „Wieder öffnen, ist abgeschlossen", und das war der Widerspruch
              auf dem Bild vom 19.10.2026. */}
          <span className={'merker ' + (zu ? 'gruen' : '')}>
            {eingereicht ? 'eingereicht' : zu ? 'abgeschlossen' : 'offen'}
          </span>
          {eingereicht && k.aufmass_eingereicht_am && (
            <span className="klein leise">
              {' '}am {datum(k.aufmass_eingereicht_am)}
            </span>
          )}
        </div>
        <div className="reihe">
          {/* SEIN AUFMASS ALS BLATT PAPIER (Punkt 24 vom 08.09.2026).
              ------------------------------------------------------------
              ORIGINALTEXT MARVIN
                „Tiefbau: sein Aufmaß als PDF herunterladen (Kopf mit
                 Adresse, SM, KLS, Termin-/Fertigstellungsdatum, Positionen,
                 Rohrangaben)"

              ER STEHT AUCH BEI EINEM ABGEGEBENEN AUFMASS DA, und das ist der
              Regelfall: gebraucht wird das Blatt, NACHDEM abgegeben wurde -
              zum Gegenhalten, zum Abheften, zum Mitnehmen auf die Baustelle.
              Die Knöpfe daneben verschwinden mit dem Absenden, dieser nicht.

              WAS DARAUFSTEHT, ENTSCHEIDET DER SERVER und nicht dieser
              Bildschirm: wer prüfen darf, bekommt die geprüfte Menge und
              eine Fusszeile, die es sagt. Eine zweite Rechnung hier wäre die
              Stelle, an der die interne Zahl in ein Blatt gerät, das
              weitergeschickt wird. */}
          <a className="knopf" href={api.aufmassBlattAdresse(auftragId)} target="_blank" rel="noreferrer">
            Als PDF
          </a>
          {/* UND DIE ROHEN ZAHLEN FÜR DEN, DER SIE GEGENHÄLT. Der Auszug
              trägt `menge_mds` und `bemerkung_mds` ohne Wenn und Aber und
              hängt deshalb an `aufmass.pruefen` - der Server weist ihn ab,
              dieser Haken ist die Höflichkeit davor. */}
          {darfPruefen && (
            <a className="knopf" href={api.aufmassAuszugAdresse(auftragId)}
               target="_blank" rel="noreferrer" download>
              Als CSV (intern)
            </a>
          )}
          {/* UND KEIN „ABSENDEN" BEI EINEM ABGESCHLOSSENEN AUFMASS (.403):
              der Knopf stand da und setzte es auf eingereicht zurück. */}
          {darfErfassen && !zu && (
            <>
              {/* DER VORSCHLAG IST INNENDIENSTSACHE (31.08.2026).
                  Marvin: „bei keinem sub sollen vorschläge kommen, die sollen
                  sich das von hand zusammenklicken."
                  Er rechnet aus dem AUFTRAG, was hineingehört - das ist
                  UNSERE Sicht auf die Baustelle. Der Sub soll aufschreiben,
                  was ER getan hat, und nicht bestätigen, was wir vermuten:
                  ein vorausgefülltes Aufmass wird beim zwanzigsten Mal
                  durchgewinkt. Der Riegel steht auch im Server; ein Knopf,
                  den die Oberfläche nicht zeichnet, ist kein Riegel. */}
              {!istSub && (
                <button className="knopf" onClick={() => setDialog('vorschlag')}>
                  Vorschlag
                </button>
              )}
              <button className="knopf" onClick={() => setDialog('abschnitt')}>
                Abschnitt anlegen
              </button>
              <button className="knopf" onClick={() => setDialog('neu')}>
                Position hinzufügen
              </button>
              <button className="knopf haupt" onClick={() => setDialog('absenden')}>
                Absenden
              </button>
            </>
          )}
          {darfPruefen && zu && (
            <button className="knopf haupt" onClick={() => setDialog('oeffnen')}>
              Wieder öffnen
              <small style={{ display: 'block', fontWeight: 400 }}>
                {eingereicht ? 'ist eingereicht' : 'ist abgeschlossen'}
              </small>
            </button>
          )}
        </div>
      </div>

      <Fehlerkasten lage={fehler} />
      {nachAbsenden && (
        <div className={'hinweis' + (/NICHT/.test(nachAbsenden) ? ' warnung' : '')}>
          {nachAbsenden}
        </div>
      )}

      {stand.befunde.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {stand.befunde.map((b: Befund, i: number) => (
            <div key={i} className={'hinweis ' + (b.hart ? 'fehler' : 'warnung')}>
              <b>{b.hart ? 'Hält auf: ' : 'Zum Nachsehen: '}</b>{b.text}
            </div>
          ))}
        </div>
      )}
      {/* WAS DER BILDSCHIRM AUF EINEN BLICK SAGEN MUSS.
          ==================================================================
          ORIGINALTEXT MARVIN (03.09.2026, zu drei Bildschirmfotos)
            „hier habe ich bereits schon mal gesagt du sollst das grau-in-grau
             entfernen. du sollst das dynamischer, innovativer und heller
             gestalten."

          Er hat es am 01.09.2026 schon einmal gesagt, für den Systemzustand
          und die Automatik; dort steht seither ein Zustandspunkt vor jedem
          Titel und eine Kachelreihe über der Tabelle. **Dieselbe Sache,
          dieselbe Antwort** - hier standen die Zahlen nirgends, und ob etwas
          zu tun ist, sah man erst, nachdem man die Tabelle gelesen hatte.

          Eine Kachel, die immer null zeigt, steht gar nicht erst da: die
          Beanstandungen erscheinen nur, wenn es welche gibt. */}
      {/* UND SIE STEHT AUCH DANN DA, WENN NOCH NICHTS ERFASST IST.
          ==================================================================
          ORIGINALTEXT MARVIN (07.09.2026, mit dem Bildschirm eines Auftrags
          OHNE eine einzige Position)
            „hier wurde das design nicht angepasst, es ist alles grau in grau,
             wirkt nicht innovativ und nicht sehr übersichtlich."

          DIE KACHELN GAB ES, UND SIE FIELEN GENAU DANN WEG, WENN DER
          BILDSCHIRM AM LEERSTEN IST. `stand.positionen.length > 0` stand
          davor - also blieb bei einem frischen Aufmaß eine graue Fläche mit
          einem roten Kasten übrig, und die einzige Zahl auf dem Bildschirm
          war keine.

          **Eine 0 ist hier keine leere Kachel, sondern die Auskunft.** Die
          Regel „eine Kachel, die IMMER null zeigt, steht gar nicht erst da"
          gilt weiter und meint etwas anderes: eine, die strukturell nie etwas
          anderes zeigen kann. Diese hier zeigt morgen 14. */}
      {(
        <div className="kachelreihe" style={{ marginBottom: 12 }}>
          <div className="kachel">
            <div className="wert">{stand.positionen.length}</div>
            <div className="was">
              {stand.positionen.length === 1 ? 'Position' : 'Positionen'}
            </div>
          </div>
          <div className="kachel">
            <div className="wert">{gruppen.length}</div>
            <div className="was">
              {gruppen.length === 1 ? 'Abschnitt' : 'Abschnitte'}
            </div>
          </div>
          {stand.befunde.length > 0 && (
            <div className={'kachel '
              + (stand.befunde.some((b: Befund) => b.hart) ? 'hart' : 'offen')}>
              <div className="wert">{stand.befunde.length}</div>
              <div className="was">
                {stand.befunde.some((b: Befund) => b.hart)
                  ? 'hält auf' : 'zum Nachsehen'}
              </div>
            </div>
          )}
          {stand.befunde.length === 0 && stand.positionen.length > 0 && (
            <div className="kachel gut">
              <div className="wert">✓</div>
              <div className="was">keine Beanstandung</div>
            </div>
          )}
        </div>
      )}

      {/* EINE TABELLE, NICHT DREI.
          ORIGINALTEXT MARVIN (01.09.2026, mit Bildschirmfoto)
            „bringe beim tiefbau das aufmaß in eine reihe damit es gleich
             aussieht"
          Hier stand je Abschnitt eine EIGENE Tabelle mit eigenem Kopf. Jede
          rechnet ihre Spaltenbreiten aus IHREM Inhalt: „Graben 1" hatte kurze
          Bezeichnungen, „ohne Abschnitt" lange, und damit standen Menge,
          Einheit und Bemerkung auf drei verschiedenen Höhen der Zeile
          untereinander. Auf seinem Bild sind es drei Blöcke, die aussehen, als
          hätten sie nichts miteinander zu tun - dabei ist es EIN Aufmaß.
          **Drei Tabellen sind drei Spaltenraster; ein Aufmass hat eines.**
          Der Abschnitt steht jetzt als Zwischenzeile darin. */}
      <table className="tabelle">
        <thead>
          <tr>
            <th style={{ width: 100 }}>OZ</th>
            <th>Leistung</th>
            {istSub ? (
              <th style={{ width: 110, textAlign: 'right' }}>Menge</th>
            ) : (
              <>
                <th style={{ width: 90, textAlign: 'right' }}>Sub</th>
                <th style={{ width: 110, textAlign: 'right' }}>MDS</th>
              </>
            )}
            <th style={{ width: 50 }}>Einh.</th>
            <th>Bemerkung</th>
            <th style={{ width: 90 }} />
          </tr>
        </thead>
        {gruppen.map((g) => (
          <tbody key={g.id ?? 'ohne'}>
            {/* DIE ZWISCHENZEILE STEHT ÜBER DIE GANZE BREITE. Sie ist eine
                Überschrift und keine Zelle - eine Bezeichnung in der ersten
                Spalte läse sich wie eine Ordnungszahl. Ein `colSpan` über
                alle Spalten hält das Raster zusammen; die Zahl richtet sich
                danach, ob der Innendienst zwei Mengenspalten sieht. */}
            <tr className="zwischenzeile">
              <th colSpan={istSub ? 6 : 7}>
                {g.name}
                {/* DER KNOPF STEHT AN EINEM LEEREN ABSCHNITT - UND JETZT
                    AUCH BEIM PARTNER.
                    ==========================================================
                    ORIGINALTEXT MARVIN (15.09.2026)
                      „die partner haben zum keinem zeitpunkt die möglichkeit,
                       erzeugte abschnitte zu löschen."

                    ORIGINALTEXT MARVIN (18.09.2026)
                      „die partner können selbst nihct die erzeugten
                       abschnitte, sprich angelegten Gräben/gruben löschen,
                       daie rechts müssen sie aber haben, gleichzeitig werden
                       dann natürlich auch die ordner unter «Dateien»
                       gelöscht"

                    Der zweite Satz hebt den ersten auf. Was den Unterschied
                    trägt, ist der Nachsatz: der Weg räumt jetzt die Ordner
                    mit weg, und damit fällt der Grund, aus dem der Riegel am
                    15.09. gesetzt wurde.

                    ER HÄNGT WEITER AN DER LEEREN GRUPPE. Ein Abschnitt mit
                    Posten wird vom Server mit 409 abgewiesen, und **ein
                    Knopf, der auf eine rote Meldung führt, ist schlimmer als
                    kein Knopf** - man drückt ihn, bekommt eine Abfuhr und
                    glaubt der Oberfläche danach nicht mehr.

                    UND FÜR DEN PARTNER NUR, SOLANGE NICHTS ABGEGEBEN IST
                    (`!eingereicht`). Genau diese Schwelle prüft der Server
                    noch einmal: ab dem Absenden ist die Gliederung Teil der
                    Abrechnung. */}
                {g.id && darfErfassen && !eingereicht
                  && g.zeilen.length === 0 && (
                  <button className="knopf klein warnung"
                          style={{ float: 'right', fontWeight: 400 }}
                          disabled={laeuft}
                          onClick={() => tu(() =>
                            api.bauabschnittLoeschen(g.id as string))}>
                    Abschnitt löschen
                  </button>
                )}
              </th>
            </tr>
            {/* EIN LEERER ABSCHNITT SAGT, DASS ER LEER IST. Eine Überschrift
                über nichts sieht aus wie eine Tabelle, die noch lädt. */}
            {g.zeilen.length === 0 && (
              <tr>
                <td colSpan={istSub ? 6 : 7} className="leise">
                  In diesem Abschnitt steht noch keine Position.
                </td>
              </tr>
            )}
            {g.zeilen.map((p: any) => (
              <Zeile
                key={p.id} p={p} laeuft={laeuft} fehler={fehler}
                istSub={istSub}
                aendern={(darfPruefen || (istSub && p.herkunft === 'sub'))
                         && !eingereicht}
                loeschen={darfErfassen && !eingereicht
                          && (!istSub || p.herkunft === 'sub')}
                speichere={(b) => tu(() => api.aufmassAendern(p.id, b))}
                entferne={(grund) => tu(() => api.aufmassLoeschen(p.id, grund))}
              />
            ))}
          </tbody>
        ))}
      </table>

      {/* EIN HINWEIS AUF EINEN WEG, DEN ES FÜR DIESEN LESER NICHT GIBT, IST
          SCHLIMMER ALS KEIN HINWEIS. Der Satz über den Vorschlag stand auch
          beim Monteur - der hat den Knopf seit dem 31.08.2026 nicht mehr,
          und er sucht ihn. */}
      {!stand.positionen.length && (
        <p className="leise">
          Zu diesem Auftrag ist noch keine einzige Position erfasst.
          {darfErfassen && (istSub
            ? ' Über „Position hinzufügen" stehen die häufigsten oben.'
            : ' Der Vorschlag weiß meistens schon, was hineingehört.')}
        </p>
      )}

      {dialog === 'neu' && (
        <NeuePosition
          lv={lv} abschnitte={stand.abschnitte} laeuft={laeuft} fehler={fehler}
          zu={() => setDialog(null)}
          speichere={(b) => tu(() => api.aufmassPosition(auftragId, b))}
        />
      )}
      {dialog === 'vorschlag' && (
        <VorschlagFenster
          auftragId={auftragId} laeuft={laeuft} fehler={fehler}
          zu={() => setDialog(null)}
          uebernimm={(pos) => tu(() => api.aufmassVorschlagNehmen(auftragId, pos))}
        />
      )}
      {dialog === 'abschnitt' && (
        <NeuerAbschnitt
          laeuft={laeuft} fehler={fehler} zu={() => setDialog(null)}
          /* DIE ZAHL KOMMT VOM SERVER. Der Name geht als Vermerk in die
             LEB-Datei; stuende die Grenze hier im Bildschirm, waere sie die
             2. Fassung derselben Zahl. */
          hoechstens={stand.vermerkHoechstens ?? undefined}
          speichere={(b) => tu(() => api.bauabschnitt(auftragId, b))}
        />
      )}
      {dialog === 'absenden' && (
        <Absenden
          befunde={stand.befunde} laeuft={laeuft} fehler={fehler}
          zu={() => setDialog(null)}
          sende={(g) => tu(async () => {
            const e = await api.aufmassAbsenden(auftragId, g);
            const t = e?.tiefbau;
            setNachAbsenden(t?.erledigt
              ? 'Aufmaß abgesendet. Der Tiefbau ist damit auch fertig gemeldet.'
              : (t?.fehlt?.length
                  ? 'Aufmaß abgesendet. Der Tiefbau ist noch NICHT fertig gemeldet: es fehlen '
                    + 'Nachweise in ' + t.fehlt.join(', ') + '. Bitte im Reiter „Dateien" nachreichen '
                    + 'und dann „Fertig melden".'
                  : null));
            return e;
          })}
        />
      )}
      {/* ZWEI WEGE ÜBER DIESELBE FRAGE LAUFEN BEIM ERSTEN NACHTRAG
          AUSEINANDER (08.09.2026).

          Hier stand `api.aufmassOeffnen`, ein zweiter Weg neben
          „Vorgang → Freigeben". Er schrieb dasselbe Ereignis, aber OHNE
          `sichtbar_fuer_sub` und ohne die Meldung an die Firma - der
          Tiefbauer erfuhr die Freigabe also nie, während sie bei der
          Montage ankam. Dieser Knopf geht jetzt denselben Weg wie der
          Menüpunkt daneben; der Weg dahinter ist ganz weggefallen. */}
      {dialog === 'oeffnen' && (
        <MitGrund
          titel="Aufmaß wieder freigeben" knopf="Freigeben"
          erklaerung="Der Grund geht an die Tiefbaufirma und steht in der
                      Auftragsinfo, für sie sichtbar. Ohne ihn weiß sie
                      nicht, was sie anders machen soll, und gibt dasselbe
                      noch einmal ab."
          laeuft={laeuft} fehler={fehler} zu={() => setDialog(null)}
          fertig={(g) => tu(async () => api.dokuSchritt(auftragId, 'aufmass',
                                                  'freigeben', g, await fragen('Die Firma per Telegram informieren, dass wieder freigegeben ist?\n\nOK = ja, pushen\nAbbrechen = nein, nicht pushen')))}
        />
      )}
    </div>
  );
}


/** Eine Zeile. Die Menge des Innendienstes ist direkt bearbeitbar. */
function Zeile({ p, istSub, aendern, loeschen, speichere, entferne, laeuft, fehler }: {
  p: any; istSub: boolean; aendern: boolean; loeschen: boolean; laeuft: boolean;
  fehler: Fehlerlage | null;
  speichere: (b: any) => Promise<boolean>;
  entferne: (grund: string) => Promise<boolean>;
}) {
  const [offen, setOffen] = useState(false);
  const [m, setM] = useState(p.menge_mds ?? p.menge_sub ?? '');
  const [bem, setBem] = useState((istSub ? p.bemerkung_sub : p.bemerkung_mds) ?? '');
  const [weg, setWeg] = useState(false);
  /* DIE MASSE STEHEN ALS TEXT IM ZUSTAND, nicht als Zahl. Wer „12," getippt
     hat, ist mitten in einer Eingabe; eine Zahl daraus zu machen löschte das
     Komma unter dem Zeiger weg. Was daraus wird, entscheidet der Server. */
  const [ma, setMa] = useState<Record<string, string>>(() => massAus(p));

  useEffect(() => {
    setM(p.menge_mds ?? p.menge_sub ?? '');
    setBem((istSub ? p.bemerkung_sub : p.bemerkung_mds) ?? '');
    setMa(massAus(p));
  }, [p.menge_mds, p.menge_sub, p.bemerkung_mds, p.bemerkung_sub, istSub,
      p.laenge, p.breite, p.tiefe, p.dicke, p.flaeche]);

  /* WER MASSE EINTRÄGT, TIPPT KEINE MENGE. Gefragt wird der Stand IM FELD und
     nicht der gespeicherte: wer gerade eine Länge eintippt, soll die Menge
     daneben nicht mehr anfassen können - sonst stehen 2 Wahrheiten über
     dieselbe Zeile nebeneinander, und welche gilt, hinge daran, welche zuletzt
     gespeichert wurde. Der RIEGEL dazu sitzt im Server; das hier ist die
     Höflichkeit davor. */
  const gesperrt = mengeGesperrt(ma);
  const felder: Massfeldanzeige[] = p.massfelder ?? [];

  /* WAS IM FELD STEHT, IST NOCH NICHT GERECHNET. Gerechnet wird auf dem
     Server; solange die Maße von den gespeicherten abweichen, ist die Zahl
     daneben die von VORHER - und eine veraltete Zahl in einem gesperrten Feld
     sieht aus wie das Ergebnis der Eingabe, die man gerade macht. Verglichen
     wird der INHALT und nicht die Adresse des Objekts: `massAus(p)` baut bei
     jedem Zeichnen ein neues. */
  const massGeaendert = JSON.stringify(ma) !== JSON.stringify(massAus(p));
  const gerechnet = gesperrt && p.formel && !massGeaendert
    ? menge(p.menge_mds ?? p.menge_sub) : '';

  /* WAS DER SERVER ABWEIST, SAGT DER BILDSCHIRM VORHER.
     Der Begründungszwang gilt auch für eine GERECHNETE Menge (siehe
     `schreiben_aufmass.ts`), und bis zum 10.09.2026 erfuhr man das erst nach
     dem Klick: roter Kasten, Zeile bleibt offen, und daneben ein Feld, dem
     niemand ansieht, dass es Pflicht ist. WELCHE Zahl herauskommt, weiss der
     Browser nicht - er rechnet die Formel mit Absicht nicht nach -, also
     steht hier, was er weiss: dass eine Abweichung eine Bemerkung braucht.
     Ein Hinweis, der zu früh kommt, kostet eine Zeile; einer, der zu spät
     kommt, kostet den Handgriff noch einmal. */
  const bemerkungFaellig = !istSub && gesperrt && !bem.trim()
    && p.menge_sub != null;

  const abweichung = p.menge_sub != null && p.menge_mds != null
    && Number(p.menge_sub) !== Number(p.menge_mds);

  if (!offen) {
    return (
      <>
        <tr className={abweichung ? 'abweichung' : undefined}>
          <td className="einfarbig">{p.oz}</td>
          <td>
            {p.bezeichnung}
            {/* DIE FORMEL STEHT DA, WO IHRE MENGE STEHT. Sie geht so in die
                Datei an die Telekom; wer die Zahl daneben nachrechnen will,
                soll sehen, woraus sie entstanden ist, ohne die Zeile
                aufzuklappen. */}
            {p.formel && (
              <div className="klein leise">Aus den Maßen: {p.formel}</div>
            )}
          </td>
          {istSub ? (
            <td style={{ textAlign: 'right' }}>{menge(p.menge_sub)}</td>
          ) : (
            <>
              <td style={{ textAlign: 'right' }}>{menge(p.menge_sub)}</td>
              <td style={{ textAlign: 'right', fontWeight: abweichung ? 600 : 400 }}>
                {menge(p.menge_mds)}
              </td>
            </>
          )}
          <td>{p.einheit}</td>
          <td className="klein">{p.bemerkung_mds ?? p.bemerkung_sub ?? ''}</td>
          <td>
            {aendern && (
              <button className="knopf klein" onClick={() => setOffen(true)}>
                ändern
              </button>
            )}
          </td>
        </tr>
        {weg && (
          <tr><td colSpan={7}>
            <MitGrund
              titel={`Position ${p.oz} entfernen`} knopf="Entfernen"
              erklaerung="Eine entfernte Position ist aus der Abrechnung heraus.
                          Der Grund gehört ins Protokoll."
              laeuft={laeuft} fehler={fehler} zu={() => setWeg(false)}
              fertig={async (g) => { await entferne(g); setWeg(false); }}
            />
          </td></tr>
        )}
      </>
    );
  }

  /* DIE MASSZEILE GEHÖRT ZU DIESER ZEILE UND IST KEINE EIGENE POSITION.
     Trägt die Zeile darüber ihre Trennlinie weiter, sehen die beiden aus wie
     zwei Positionen - dabei entsteht die Menge der einen aus den Maßen der
     anderen. Die Klasse steht deshalb an der Zeile und nicht am Kasten. */
  const zeile = (
    <tr className={felder.length ? 'mitmassen' : undefined}>
      <td className="einfarbig">{p.oz}</td>
      <td>
        {p.bezeichnung}
        {!istSub && p.menge_sub != null && (
          <div className="klein leise">
            Der Sub hat {menge(p.menge_sub, p.einheit)} gemeldet.
          </div>
        )}
      </td>
      {!istSub && (
        <td style={{ textAlign: 'right' }}>{menge(p.menge_sub)}</td>
      )}
      <td>
        {/* EINE GESPERRTE ZAHL IST IMMER NOCH EINE ZAHL. Hier stand ein leeres
            Feld mit dem Platzhalter „wird gerechnet", und der war in 90
            Bildpunkten als „wird gerech" abgeschnitten - ein abgeschnittener
            Satz ist ein halber. Gezeigt wird deshalb das ERGEBNIS der Formel,
            und der Platzhalter bleibt für den Fall, dass noch keines dasteht.
            Die Breite ist NACHGEMESSEN und nicht gerechnet. */}
        <input value={gesperrt ? gerechnet : m} disabled={gesperrt}
               placeholder={gesperrt && !gerechnet ? 'aus den Maßen' : undefined}
               style={{ width: 120, textAlign: 'right' }}
               className={fehler?.feld === 'masse' ? 'falsch' : undefined}
               onChange={(e) => setM(e.target.value)} />
      </td>
      <td>{p.einheit}</td>
      <td>
        <input value={bem}
               placeholder={istSub ? 'Bemerkung' : 'warum diese Menge?'}
               style={{ width: '100%' }}
               className={fehler?.feld === (istSub ? 'bemerkung_sub' : 'bemerkung_mds')
                          ? 'falsch' : undefined}
               onChange={(e) => setBem(e.target.value)} />
      </td>
      <td>
        <div className="reihe">
          {/* ZWEI SPALTEN, ZWEI SCHREIBER. Der Sub berichtigt seine eigene
              Angabe, der Innendienst setzt die geprüfte. Vorher schickte
              dieses Feld immer `menge_mds`, und der Server nahm es vom Sub
              gar nicht erst an. */}
          <button className="knopf klein haupt" disabled={laeuft}
                  onClick={async () => {
                    /* DIE MASSE GEHEN NUR MIT, WO ES SIE GIBT. Ein `masse` im
                       Rumpf einer Position in `St` wäre eine Angabe, die der
                       Server zu Recht abweist - und die Zeile liesse sich
                       danach gar nicht mehr speichern. */
                    const ok = await speichere({
                      ...(istSub
                        ? { menge_sub: m, bemerkung_sub: bem }
                        : { menge_mds: m, bemerkung_mds: bem }),
                      ...(felder.length ? { masse: massHinaus(ma) } : {}),
                    });
                    if (ok) setOffen(false);
                  }}>
            ok
          </button>
          <button className="knopf klein" disabled={laeuft}
                  onClick={() => {
                    setOffen(false);
                    setM(p.menge_mds ?? p.menge_sub ?? '');
                    setMa(massAus(p));
                  }}>
            zurück
          </button>
          {loeschen && (
            <button className="knopf klein" disabled={laeuft}
                    onClick={() => { setOffen(false); setWeg(true); }}>
              weg
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  /* KEINE ZWEITE ZEILE, WO ES NICHTS ZU RECHNEN GIBT. Bei einer Position in
     `St` oder `LE` ist ein Stück ein Stück; 5 Felder darunter wären 5 Fragen,
     deren Antworten niemand liest - und das Mengenfeld daneben bliebe ohne
     Grund gesperrt, sobald jemand eine davon ausfüllt. */
  if (!felder.length) return zeile;

  return (
    <>
      {zeile}
      {/* DIE MASSE STEHEN UNTER IHRER ZEILE UND ÜBER DIE GANZE BREITE.
          ORIGINALTEXT MARVIN (10.09.2026)
            „der tiefbauer tippt bei der entsprechenden position ein, die zur
             errechnung notwendig sind. dann kommt ein wert raus in m2, was
             dann alles zusammen in dem LEB eingetragen wird."
          In eine der 7 Spalten passen sie nicht: es sind bis zu 5 Felder mit
          je einer Beschriftung, und die Tabelle daneben trägt die Mengen. Eine
          eigene Zeile hält das Raster zusammen. */}
      <tr>
        <td colSpan={istSub ? 6 : 7} className="massfelder">
          <div className="reihe">
            {felder.map(({ feld, rolle }) => (
              <label key={feld} className="massfeld">
                <span className="klein">{feldname(feld)}</span>
                <input value={ma[feld] ?? ''} inputMode="decimal"
                       style={{ width: 90, textAlign: 'right' }}
                       className={fehler?.feld === 'masse' && rolle === 'faktor'
                                  ? 'falsch' : undefined}
                       onChange={(e) => setMa(
                         { ...ma, [feld]: e.target.value })} />
                <span className="klein leise">{rollentext(rolle)}</span>
              </label>
            ))}
          </div>
          {/* DREI AUSGÄNGE, UND JEDER SAGT ETWAS ANDERES: es gibt eine Formel,
              es fehlt noch ein Maß, oder hier gibt es nichts zu rechnen. Ein
              Satz für alle drei beantwortet keinen von ihnen. */}
          <div className="klein leise">
            {formelsatz(p.formel, p.einheit, p.faktorfelder)}
            {' '}{zulagesatz(felder)}
          </div>
          {/* EIN HINWEIS IST KEIN RIEGEL. Eine eingetragene Fläche, die nicht
              zu Länge mal Breite passt, ist ein BEFUND - es kann ein Graben
              mit einer Ausbuchtung sein. Sie wird gemeldet und nicht
              überschrieben. */}
          {p.formelhinweis && (
            <div className="hinweis warnung">{p.formelhinweis}</div>
          )}
          {/* DER BEGRÜNDUNGSZWANG STEHT VOR DEM KLICK UND NICHT DANACH.
              Er steht hier und nicht am Bemerkungsfeld: es geht um die
              gerechnete Menge, und die entsteht in dieser Zeile. Er
              verschwindet, sobald eine Bemerkung dasteht - ein Kasten, der
              immer dasteht, wird auch an dem Tag nicht gelesen, an dem er
              zutrifft. */}
          {bemerkungFaellig && (
            <div className="hinweis">
              Weicht die gerechnete Menge von den{' '}
              {menge(p.menge_sub, p.einheit)} ab, die der Sub gemeldet hat,
              gehört eine Bemerkung dazu - sonst nimmt der Server die Zeile
              nicht an.
            </div>
          )}
        </td>
      </tr>
    </>
  );
}


/**
 * Die Positionsliste in drei Gruppen.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „sowohl beim tiefbau als auch bei uns im backoffice sollen die am
 *    häufigsten verwendeten psotionen nach oben zusammengefasst werden, dass
 *    soll automatsich erfolgen, die auflistung kann sich ja ändern. mache die
 *    10 häufigsten"
 *   „bei der montage gibt es feste positionen, die wir im backoffice eingeben
 *    und festlegen, die dann bei den monteuren unabhängig von den normalen LV
 *    Positionen stehen."
 *
 * DER RANG WIRD GERECHNET UND KOMMT VOM SERVER (`v_lv_haeufig`, Migration
 * 080). Gezählt werden AUFTRÄGE und nicht Zeilen - sonst gewinnt die
 * Position, die in EINEM Auftrag zwanzigmal steht, gegen die, die in
 * zweihundert Aufträgen einmal steht, und genau die zweite sucht man.
 *
 * EINE POSITION STEHT IN GENAU EINER GRUPPE. Stünde eine häufige
 * Montageposition in beiden, wäre die Liste länger als der Katalog, und wer
 * sie in der zweiten Gruppe sucht, findet sie nicht - er hat sie in der
 * ersten schon überblättert. Die feste Montageposition gewinnt: sie ist eine
 * Festlegung des Backoffice, der Rang nur eine Beobachtung.
 */
export function positionenGeordnet(lv: any[]) {
  /* ZWEI MERKMALE, EINE GRUPPE - UND DAS IST KEIN VERSEHEN.
     ========================================================================
     ORIGINALTEXT MARVIN (18.09.2026)
       „nein, es sind eigene, freie positionen die ich anlegen will, egal wie
        viele. und die kann ich dann zuweisen zu jeder firma einzeln manuell"

     `eigene_position` (Migration 265) und `montage_fest` sind seitdem 2
     verschiedene Sachen: das eine heisst „diese Position gibt es im
     Verzeichnis der Telekom nicht", das andere „diese Position der Telekom
     zaehlt in der Auswertung zum Block Montage". Getrennt gehoeren sie in der
     DATENBANK, weil eine Auswertung sie sonst nicht mehr auseinanderhalten
     kann - **in der AUSWAHL gehoeren sie zusammen**: beide sind das, wonach
     der Monteur ganz oben greift, und 2 Gruppen mit fast gleichem Namen
     untereinander sind schlimmer als eine.

     DIE REIHENFOLGE INNERHALB DER GRUPPE: die eigenen zuerst. Sie sind die
     wenigeren und die, die Marvin selbst angelegt hat. */
  const eigen = (l: any) => !!l.eigene_position || !!l.montage_fest;
  const feste = lv.filter(eigen)
    .sort((a, b) => Number(!!b.eigene_position) - Number(!!a.eigene_position));
  const oft = lv.filter((l) => !eigen(l) && l.oft)
    .sort((a, b) => (a.rang ?? 99) - (b.rang ?? 99));
  const rest = lv.filter((l) => !eigen(l) && !l.oft);
  const zeile = (l: any) => ({
    wert: l.oz,
    text: `${l.oz}  ${l.text}`,
  });
  return [
    /* DIE GRUPPE HEISST, WAS SIE IST.
       ======================================================================
       ORIGINALTEXT MARVIN (18.09.2026)
         „mir fehlen immernoch bei der montage die eigenen positionen zur
          auswahl. diese posionen trage ich selbst ein mit eigenen preisen zu
          jedem sub. wo ist der bereich?"

       SIE STANDEN DIE GANZE ZEIT DA, und zwar ganz oben - unter der
       Überschrift „Montage". Angelegt werden sie unter Stammdaten,
       Leistungskatalog, mit dem Haken „Feste Montageposition". **Drei Namen
       für eine Sache, und keiner davon der, unter dem man sie sucht** -
       derselbe Fall wie beim Reiter „Dokumente erzeugen" am 02.09.2026.
       Die Gruppe heisst jetzt so, wie Marvin sie nennt. */
    ...feste.map((l) => ({ ...zeile(l), gruppe: 'Eigene Positionen' })),
    ...oft.map((l) => ({ ...zeile(l), gruppe: 'Am häufigsten' })),
    ...rest.map((l) => ({ ...zeile(l), gruppe: 'Alle übrigen' })),
  ];
}

/* `vermerk` (.403): im LEB-Reiter heißt das Feld „Vermerk" und geht in die
   Datei. Marvin, 19.10.2026: „Wenn ich hier einen Vermerk schreibe, dann soll
   das auch der Vermerk sein, der in die Datei rübergeht. Sonst schreibe ich
   danach nämlich den Vermerk erneut." Im Aufmaß-Reiter bleibt es die
   Bemerkung des Innendienstes, die nicht an die Telekom geht. */
export function NeuePosition({ lv, abschnitte, zu, speichere, laeuft, fehler, vermerk = false }: any) {
  const [oz, setOz] = useState('');
  const [m, setM] = useState('');
  const [ab, setAb] = useState('');
  const [bem, setBem] = useState('');
  const gewaehlt = lv.find((l: any) => l.oz === oz);

  return (
    <Dialog titel="Position hinzufügen" laeuft={laeuft} zu={zu} breite={620}>
      <Fehlerkasten lage={fehler} />
      <Auswahl
        name="oz" titel="Leistung" pflicht wert={oz} setze={setOz} breite="100%"
        fehler={fehler}
        optionen={positionenGeordnet(lv)}
      />
      <div className="feldreihe">
        <Text name="menge" titel={`Menge${gewaehlt ? ' in ' + gewaehlt.einheit : ''}`}
              pflicht wert={m} setze={setM} fehler={fehler} breite={160} />
        <Auswahl
          name="bauabschnitt_id" titel="Bauabschnitt" wert={ab} setze={setAb}
          leerText="ohne Abschnitt" fehler={fehler}
          optionen={abschnitte.map((a: any) => ({ wert: a.id, text: a.bezeichnung }))}
          hinweis={gewaehlt?.erlaubte_abschnitte?.length
            ? 'Diese Leistung gehört an einen ' + gewaehlt.erlaubte_abschnitte.join(' oder ')
            : undefined}
        />
      </div>
      {vermerk
        ? <Text name="vermerk" titel="Vermerk (geht so in die LEB-Datei, höchstens 40 Zeichen)" wert={bem} setze={(t: string) => setBem(t.slice(0, 40))}
                breite="100%" fehler={fehler} hinweis={bem.length ? `${bem.length} von 40 Zeichen` : undefined} />
        : <Text name="bemerkung" titel="Bemerkung" wert={bem} setze={setBem}
                breite="100%" fehler={fehler} />}
      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt" disabled={laeuft || !oz || !m}
                onClick={() => speichere(vermerk
                  ? { oz, menge: m, bauabschnitt_id: ab || null, vermerk: bem }
                  : { oz, menge: m, bauabschnitt_id: ab || null, bemerkung: bem })}>
          {laeuft ? 'Einen Moment' : 'Hinzufügen'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


function NeuerAbschnitt({ zu, speichere, laeuft, fehler, hoechstens }: any) {
  const [typ, setTyp] = useState('graben');
  const [bez, setBez] = useState('');
  return (
    <Dialog titel="Bauabschnitt anlegen" laeuft={laeuft} zu={zu} breite={520}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise">
        Ein Abschnitt ist ein Graben oder eine Grube. Die Prüfung rechnet je
        Abschnitt: eine Oberfläche kann nur so groß sein wie der Graben, in dem
        sie liegt.
      </p>
      <div className="feldreihe">
        <Auswahl name="typ" titel="Art" pflicht wert={typ} setze={setTyp}
                 fehler={fehler} leerText=""
                 optionen={[{ wert: 'graben', text: 'Graben' },
                            { wert: 'grube', text: 'Grube' }]} />
        {/* DER NAME GEHT ALS VERMERK IN DIE VERGABE, und die nimmt dort
            40 Zeichen.

            ORIGINALTEXT MARVIN (15.09.2026, Bild 11)
              „in der vergabe sind bei dem vermerk, also abschnitt nur 40
               Zeihen erlaubt, trage das auch bei den tiefbauern in den
               feldern ein als maximale eingabe."

            DAS FELD NIMMT NICHT MEHR AN, statt hinterher abzuweisen - und
            der Satz darunter sagt, WOHIN der Name geht. Ohne ihn sieht eine
            Grenze an einem Namensfeld nach Schikane aus. */}
        <Text name="bezeichnung" titel="Bezeichnung" wert={bez} setze={setBez}
              platzhalter="z. B. Gehweg vor Haus 15" fehler={fehler}
              hoechstens={hoechstens} breite={280} />
      </div>
      {hoechstens ? (
        <p className="klein leise" style={{ marginTop: 6 }}>
          Der Name geht als Vermerk in die LEB-Datei. Die Vergabe nimmt dort
          {' '}{hoechstens} Zeichen, deshalb endet das Feld dort.
        </p>
      ) : null}
      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt" disabled={laeuft}
                onClick={() => speichere({ typ, bezeichnung: bez })}>
          Anlegen
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


/**
 * Der Vorschlag.
 *
 * Was ohne Menge kommt, bleibt ohne Menge: eine offene Position wird NICHT
 * geschrieben. Früher ist auf diese Weise wiederholt eine 0 in die
 * Abrechnung gelaufen.
 */
function VorschlagFenster({ auftragId, zu, uebernimm, laeuft, fehler }: any) {
  const [art, setArt] = useState('komplett');
  const [v, setV] = useState<any>(null);
  const [mengen, setMengen] = useState<Record<string, string>>({});
  const [holt, setHolt] = useState(true);

  useEffect(() => {
    setHolt(true);
    api.aufmassVorschlag(auftragId, art).then((d) => {
      setV(d);
      const m: Record<string, string> = {};
      for (const p of d.positionen) m[p.oz] = p.menge != null ? String(p.menge) : '';
      setMengen(m);
    }).catch(() => {}).finally(() => setHolt(false));
  }, [art, auftragId]);

  const nehmen = (v?.positionen ?? []).filter((p: any) => {
    const m = (mengen[p.oz] ?? '').replace(',', '.');
    return m !== '' && Number(m) > 0;
  });

  return (
    <Dialog titel="Vorschlag" laeuft={laeuft} zu={zu} breite={720}>
      <Fehlerkasten lage={fehler} />
      <Auswahl name="art" titel="Art der Abrechnung" wert={art} setze={setArt}
               leerText="" breite={220}
               optionen={[{ wert: 'komplett', text: 'Komplettabrechnung' },
                          { wert: 'teil', text: 'Teilabrechnung' },
                          { wert: 'schluss', text: 'Schlussabrechnung' }]} />
      {holt && <p className="leise">Einen Moment.</p>}
      {v?.hinweise?.map((h: string, i: number) => (
        <div key={i} className="hinweis">{h}</div>
      ))}
      {v && (
        <table className="tabelle">
          <thead>
            <tr><th style={{ width: 100 }}>OZ</th><th>Leistung</th>
                <th style={{ width: 110 }}>Menge</th><th>Herkunft</th></tr>
          </thead>
          <tbody>
            {v.positionen.map((p: any) => (
              <tr key={p.oz + (p.abschnittTyp ?? '')}>
                <td className="einfarbig">{p.oz}</td>
                <td>{p.bezeichnung}
                  {p.abschnittTyp === 'grube' && (
                    <span className="klein leise"> (legt eine Montagegrube an)</span>
                  )}
                </td>
                <td>
                  <input value={mengen[p.oz] ?? ''} style={{ width: 90 }}
                         placeholder={p.einheit}
                         onChange={(e) =>
                           setMengen({ ...mengen, [p.oz]: e.target.value })} />
                </td>
                <td className="klein leise">{p.herkunft ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="klein leise" style={{ marginTop: 8 }}>
        Was ohne Menge bleibt, wird nicht übernommen. Eine offene Position ist
        besser als eine Null in der Abrechnung.
      </p>
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt" disabled={laeuft || !nehmen.length}
                onClick={() => uebernimm(nehmen.map((p: any) => ({
                  ...p, menge: mengen[p.oz] })))}>
          {nehmen.length} von {v?.positionen?.length ?? 0} übernehmen
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


function Absenden({ befunde, zu, sende, laeuft, fehler }: any) {
  const [grund, setGrund] = useState('');
  const hart = befunde.filter((b: Befund) => b.hart);
  return (
    <Dialog titel="Aufmaß absenden" laeuft={laeuft} zu={zu} breite={620}>
      <Fehlerkasten lage={fehler} />
      {hart.length === 0 && befunde.length === 0 && (
        <p>Keine Beanstandung. Das Aufmaß kann so eingereicht werden.</p>
      )}
      {befunde.filter((b: Befund) => !b.hart).map((b: Befund, i: number) => (
        <div key={i} className="hinweis warnung">{b.text}</div>
      ))}
      {hart.map((b: Befund, i: number) => (
        <div key={i} className="hinweis fehler">{b.text}</div>
      ))}
      {hart.length > 0 && (
        <Text name="begruendung" titel="Begründung" mehrzeilig
              pflicht wert={grund} setze={setGrund} fehler={fehler}
              hinweis="Ein harter Befund hält auf. Er lässt sich übergehen, aber
                       nur schriftlich, und die Begründung geht mit in die Akte." />
      )}
      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt"
                disabled={laeuft || (hart.length > 0 && grund.trim().length < 10)}
                onClick={() => sende(grund || undefined)}>
          {laeuft ? 'Einen Moment' : 'Absenden'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


/** Ein kleiner Dialog, der nach einem Grund fragt und ihn verlangt. */
export function MitGrund(
  { titel, knopf, erklaerung, zu, fertig, laeuft, fehler }: {
    titel: string; knopf: string; erklaerung?: string;
    zu: () => void; fertig: (grund: string) => void;
    laeuft: boolean; fehler: Fehlerlage | null;
  }
) {
  const [grund, setGrund] = useState('');
  return (
    <Dialog titel={titel} laeuft={laeuft} zu={zu} breite={520}>
      <Fehlerkasten lage={fehler} />
      {erklaerung && <p className="klein leise">{erklaerung}</p>}
      <Text name="grund" titel="Grund" pflicht mehrzeilig
            wert={grund} setze={setGrund} fehler={fehler} />
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt" disabled={laeuft || grund.trim().length < 3}
                onClick={() => fertig(grund)}>
          {laeuft ? 'Einen Moment' : knopf}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}
